import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import { motion, useInView, animate } from 'framer-motion';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import { QRIcon } from '../../components/common/Icons';
import {
  FiCheck,
  FiMinus,
  FiSmartphone,
  FiGrid,
  FiCreditCard,
  FiMessageSquare,
  FiArrowRight,
  FiShare2
} from 'react-icons/fi';
import '../../styles/landing.css';

gsap.registerPlugin(ScrollTrigger);

const MotionLink = motion(Link);

// Decorative QR tag pattern — not a scannable code, just a visual echo
// of the "download your QR code" feature, used in the hero scene.
const QR_PATTERN = [
  1, 1, 1, 0, 1,
  1, 0, 1, 0, 0,
  1, 1, 1, 0, 1,
  0, 0, 0, 1, 0,
  1, 0, 1, 0, 1
];

// Each plan drives both the header row and the price row, so the numeric
// value can be animated with AnimatedCounter instead of being baked into text.
const PLANS = [
  { key: 'free', name: 'Free', price: 0, suffix: '/forever' },
  { key: 'basic', name: 'Basic', price: 499, suffix: '/year', highlight: true, badge: 'Most Popular' },
  { key: 'pro', name: 'Pro', price: 999, suffix: '/year' }
];

const PRICING_ROWS = [
  { label: 'Products', free: 'Up to 5', basic: 'Unlimited', pro: 'Unlimited' },
  { label: 'Themes', free: 'Minimal (1)', basic: 'All 5 designer themes', pro: 'All 5 + premium' },
  { label: 'Digital visiting card', free: false, basic: true, pro: true },
  { label: 'QR code', free: 'Basic', basic: 'Print quality', pro: 'Print quality' },
  { label: 'Enquiry management', free: false, basic: 'Full', pro: 'Full' },
  { label: 'Custom URL slug', free: false, basic: false, pro: true },
  { label: 'Priority analytics', free: false, basic: false, pro: true },
];

const KIT_ROWS = [
  {
    tag: '01 / 03',
    eyebrow: 'Piece one — the page',
    icon: FiSmartphone,
    title: 'Digital Business Page',
    desc: 'A mini website showcasing your logo, description, business hours, address, and quick contact buttons — the address you send customers to first.',
    img: '/assets/body3.png',
    reverse: false
  },
  {
    tag: '02 / 03',
    eyebrow: 'Piece two — the catalogue',
    icon: FiGrid,
    title: 'Product Catalogue',
    desc: 'Organize your products and services with categories, discount prices, images, and a direct WhatsApp enquiry button on every item.',
    img: '/assets/body2.png',
    reverse: true
  },
  {
    tag: '03 / 03',
    eyebrow: 'Piece three — the card',
    icon: FiCreditCard,
    title: 'Digital Visiting Card',
    desc: 'Share an interactive digital business card with your contact details, phone, email, and one-tap save-to-contacts buttons.',
    img: '/assets/body4.png',
    reverse: false
  }
];

const HOW_STEPS = [
  ['Create Account', 'Sign up in 30 seconds'],
  ['Add Details', 'Enter phone, address & hours'],
  ['Add Products', 'Upload images & prices'],
  ['Choose Design', 'Pick from 5 themes'],
  ['Publish Page', 'Get instant custom URL'],
  ['Share Your Link', 'Share link & QR code'],
];

const PricingCell = ({ value }) => {
  if (value === true) return <FiCheck className="cell-check" />;
  if (value === false) return <FiMinus className="cell-dash" />;
  return <span>{value}</span>;
};

// Counts up from 0 to `value` once it scrolls into view. Used for the
// pricing digits — real numbers already in PLANS, nothing fabricated.
const AnimatedCounter = ({ value, prefix = '', suffix = '', duration = 1.1 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setDisplay(value);
      return;
    }
    const controls = animate(0, value, {
      duration,
      ease: 'easeOut',
      onUpdate: (v) => setDisplay(Math.round(v))
    });
    return () => controls.stop();
  }, [isInView, value, duration]);

  return (
    <span ref={ref} className="price-counter">
      {prefix}
      {display}
      {suffix}
    </span>
  );
};

const LandingPage = () => {
  const pageRef = useRef(null);

  // Smooth scroll. If Lenis is already initialized higher up (e.g. App.jsx),
  // this guard skips a duplicate instance — move this block up to the app
  // root once you wire smooth scroll in globally.
  useEffect(() => {
    if (window.__lenisInstance) return undefined;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return undefined;

    const lenis = new Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t))
    });
    window.__lenisInstance = lenis;

    lenis.on('scroll', ScrollTrigger.update);

    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenisInstance = null;
    };
  }, []);

  // Scroll-choreographed reveals, kept in a single gsap.context so everything
  // is cleaned up together on unmount.
  useLayoutEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const ctx = gsap.context(() => {
      if (prefersReducedMotion) {
        gsap.set(
          '.hero-pill, .hero-title, .hero-subtitle, .hero-buttons, .hero-note, .hero-display, .kit-visual, .kit-copy, .kit-support-card, .path-step, .pricing-table-wrap, .cta-copy > *, .cta-qr',
          { opacity: 1, x: 0, y: 0, scale: 1 }
        );
        return;
      }

      // Hero entrance
      gsap.timeline({ defaults: { ease: 'power3.out' } })
        .from('.hero-pill', { opacity: 0, y: 16, duration: 0.5 })
        .from('.hero-title', { opacity: 0, y: 26, duration: 0.7 }, '-=0.3')
        .from('.hero-subtitle', { opacity: 0, y: 16, duration: 0.6 }, '-=0.4')
        .from('.hero-buttons', { opacity: 0, y: 16, duration: 0.6 }, '-=0.4')
        .from('.hero-note', { opacity: 0, duration: 0.5 }, '-=0.3')
        .from('.hero-display', { opacity: 0, scale: 0.94, duration: 0.9 }, '-=0.6');

      // Ambient floating orb behind the hero visual
      gsap.to('.hero-orb', {
        y: 36,
        x: 12,
        rotation: 18,
        duration: 7,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut'
      });

      // Kit rows: visual scales in, copy slides in from the direction the row faces
      gsap.utils.toArray('.kit-row').forEach((row) => {
        const visual = row.querySelector('.kit-visual');
        const copy = row.querySelector('.kit-copy');
        const isReversed = row.classList.contains('kit-row-reverse');

        gsap.from(visual, {
          opacity: 0,
          scale: 0.92,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: { trigger: row, start: 'top 78%' }
        });
        gsap.from(copy, {
          opacity: 0,
          x: isReversed ? 36 : -36,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: { trigger: row, start: 'top 78%' }
        });
      });

      // Support cards
      gsap.from('.kit-support-card', {
        opacity: 0,
        y: 28,
        duration: 0.6,
        stagger: 0.15,
        ease: 'power3.out',
        scrollTrigger: { trigger: '.kit-support-grid', start: 'top 82%' }
      });

      // How-it-works steps + the connecting progress line that draws as you scroll
      gsap.from('.path-step', {
        opacity: 0,
        y: 24,
        duration: 0.6,
        stagger: 0.1,
        ease: 'power3.out',
        scrollTrigger: { trigger: '.path-track', start: 'top 82%' }
      });
      gsap.fromTo(
        '.path-line-progress',
        { scaleX: 0 },
        {
          scaleX: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: '.path-track',
            start: 'top 75%',
            end: 'bottom 65%',
            scrub: 0.4
          }
        }
      );

      // Pricing table
      gsap.from('.pricing-table-wrap', {
        opacity: 0,
        y: 36,
        duration: 0.8,
        ease: 'power3.out',
        scrollTrigger: { trigger: '.pricing-table-wrap', start: 'top 82%' }
      });

      // CTA
      gsap.from('.cta-copy > *', {
        opacity: 0,
        y: 18,
        duration: 0.6,
        stagger: 0.1,
        ease: 'power3.out',
        scrollTrigger: { trigger: '.cta-section', start: 'top 80%' }
      });
      gsap.from('.cta-qr', {
        opacity: 0,
        scale: 0.85,
        duration: 0.7,
        ease: 'back.out(1.4)',
        scrollTrigger: { trigger: '.cta-section', start: 'top 76%' }
      });
      gsap.to('.cta-glow', {
        x: 24,
        y: -18,
        duration: 8,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut'
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="landing-page" ref={pageRef}>
      <Navbar />

      {/* HERO SECTION */}
      <section className="hero-section">
        <div className="container hero-grid">
          <div className="hero-copy">
            <div className="hero-pill">
              <span>✨ Launch Your Digital Storefront Today</span>
            </div>

            <h1 className="hero-title">
              Your Business Deserves a <br /> Digital Presence.
            </h1>

            <p className="hero-subtitle">
              Create a beautiful business page, digital catalogue, visiting card, and QR code — without coding.
            </p>

            <div className="hero-buttons">
              <MotionLink
                to="/register"
                className="btn btn-primary btn-lg"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.97 }}
              >
                Create Your Business Page <FiArrowRight />
              </MotionLink>
              <motion.a
                href="/business/abc-textiles"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary btn-lg"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.97 }}
              >
                View Demo
              </motion.a>
            </div>

            <p className="hero-note">No coding needed · Live in minutes · Cancel anytime</p>
          </div>

          <div className="hero-display" aria-hidden="true">
            <div className="hero-orb" />
            <div className="counter-scene">
              <img src='/assets/head.png' alt='' />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES — KIT SHOWCASE */}
      <section id="features" className="kit-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">All-In-One Toolkit</span>
            <h2 className="section-title">Everything Your Business Needs to Grow Online</h2>
            <p className="section-desc">
              Replace complex websites with a streamlined, mobile-first business kit built for high conversion.
            </p>
          </div>

          {KIT_ROWS.map(({ tag, eyebrow, icon: Icon, title, desc, img, reverse }) => (
            <div className={`kit-row ${reverse ? 'kit-row-reverse' : ''}`} key={title}>
              <div className="kit-visual">
                <span className="kit-visual-tag">{tag}</span>
                <img src={img} alt={title} />
              </div>
              <div className="kit-copy">
                <span className="kit-eyebrow"><Icon /> {eyebrow}</span>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            </div>
          ))}

          <div className="kit-support-grid">
            {[
              { Icon: QRIcon, title: 'QR Code Generator', desc: 'Download high-resolution, print-ready QR codes linking straight to your page — built for counters and flyers.' },
              { Icon: FiMessageSquare, title: 'Enquiry Form', desc: 'Capture customer leads into your dashboard and respond instantly with pre-filled WhatsApp shortcuts.' },
              { Icon: FiShare2, title: '100% Mobile Friendly', desc: 'Optimized for WhatsApp sharing and mobile browsing, so customers open your link instantly without lag.' }
            ].map(({ Icon, title, desc }) => (
              <motion.div
                className="kit-support-card"
                key={title}
                whileHover={{ y: -6 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <motion.div
                  className="feature-icon-box"
                  whileHover={{ rotate: -8, scale: 1.08 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 12 }}
                >
                  <Icon />
                </motion.div>
                <h4>{title}</h4>
                <p>{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — CONNECTED PATH */}
      <section id="how-it-works" className="how-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Simple 6-Step Workflow</span>
            <h2 className="section-title">How Digital Business Kit Works</h2>
          </div>

          <div className="path-track">
            <div className="path-line-track" aria-hidden="true">
              <div className="path-line-progress" />
            </div>
            {HOW_STEPS.map(([title, desc], i) => (
              <div className="path-step" key={title}>
                <motion.div
                  className="path-num"
                  whileHover={{ scale: 1.12 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 14 }}
                >
                  {i + 1}
                </motion.div>
                <h4>{title}</h4>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING — COMPARISON TABLE */}
      <section id="pricing" className="pricing-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Affordable Pricing</span>
            <h2 className="section-title">Pick the Plan That Fits Your Scale</h2>
          </div>

          <div className="pricing-table-wrap">
            <table className="pricing-table">
              <thead>
                <tr>
                  <th className="row-label-col" />
                  {PLANS.map((plan) => (
                    <th key={plan.key} className={plan.highlight ? 'col-highlight' : ''}>
                      {plan.name}
                      {plan.badge && <span className="pricing-badge">{plan.badge}</span>}
                    </th>
                  ))}
                </tr>
                <tr className="price-row">
                  <th className="row-label-col">Price</th>
                  {PLANS.map((plan) => (
                    <td key={plan.key} className={plan.highlight ? 'col-highlight' : ''}>
                      ₹<AnimatedCounter value={plan.price} />
                      <span className="price-suffix">{plan.suffix}</span>
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PRICING_ROWS.map((row) => (
                  <tr key={row.label}>
                    <th className="row-label-col">{row.label}</th>
                    {PLANS.map((plan) => (
                      <td key={plan.key} className={plan.highlight ? 'col-highlight' : ''}>
                        <PricingCell value={row[plan.key]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th className="row-label-col" />
                  {PLANS.map((plan) => (
                    <td key={plan.key} className={plan.highlight ? 'col-highlight' : ''}>
                      <MotionLink
                        to="/register"
                        className={`btn btn-sm ${plan.highlight ? 'btn-primary' : 'btn-secondary'}`}
                        whileHover={{ y: -2 }}
                        whileTap={{ scale: 0.96 }}
                      >
                        {plan.key === 'free' ? 'Get Started' : `Select ${plan.name}`}
                      </MotionLink>
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="cta-section">
        <div className="container cta-grid">
          <div className="cta-copy">
            <h2>Build your digital business presence today.</h2>
            <p>Join small business owners across Tamil Nadu and Kerala showcasing their products online.</p>
            <MotionLink
              to="/register"
              className="btn btn-primary btn-lg"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              Create Your Free Page Now <FiArrowRight />
            </MotionLink>
          </div>
          <div className="cta-qr" aria-hidden="true">
            <div className="cta-glow" />
            <div className="mock-qr-grid mock-qr-grid-lg">
              {QR_PATTERN.map((cell, i) => (
                <span key={i} className={cell ? 'is-on' : ''} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;